import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Users, Search, MapPin, Mail, Phone, Calendar, Building2, Heart, MessageSquare, Download, Trash2, Plus, X, Check, KeyRound } from 'lucide-react';
import { API, getToken } from './shared';

const UsersTab = ({ users: initialUsers }) => {
  const [users, setUsers] = useState(initialUsers);
  const [userSearch, setUserSearch] = useState('');
  const [userCityFilter, setUserCityFilter] = useState('');
  const [exporting, setExporting] = useState(false);
  const [assigningUser, setAssigningUser] = useState(null);
  const [allListings, setAllListings] = useState([]);
  const [userListings, setUserListings] = useState([]);
  const [selectedListings, setSelectedListings] = useState(new Set());
  const [listingSearch, setListingSearch] = useState('');
  const [loadingAssign, setLoadingAssign] = useState(false);
  const [resetUser, setResetUser] = useState(null);
  const [resetPassword, setResetPassword] = useState('');
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    setUsers(initialUsers);
  }, [initialUsers]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchUsers(userSearch, userCityFilter);
    }, 300);
    return () => clearTimeout(debounce);
  }, [userSearch, userCityFilter]);

  const fetchUsers = async (search, city) => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (city) params.append('city', city);
      const res = await axios.get(`${API}/admin/users?${params.toString()}`, { headers: { Authorization: `Bearer ${getToken()}` } });
      setUsers(res.data);
    } catch (error) { console.error('Error fetching users:', error); }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete user "${userName}"? Their listings will be unlinked.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { headers: { Authorization: `Bearer ${getToken()}` } });
      setUsers(prev => prev.filter(u => u.id !== userId));
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Failed to delete user');
    }
  };

  const handleResetPassword = async () => {
    if (!resetPassword || resetPassword.length < 6) {
      alert('Password must be at least 6 characters');
      return;
    }
    setResetting(true);
    try {
      await axios.post(`${API}/admin/users/${resetUser.id}/reset-password`, { new_password: resetPassword }, { headers: { Authorization: `Bearer ${getToken()}` } });
      alert(`Password reset successfully for ${resetUser.name}`);
      setResetUser(null);
      setResetPassword('');
    } catch (error) {
      console.error('Error resetting password:', error);
      alert(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResetting(false);
    }
  };

  const openAssignModal = async (user) => {
    setAssigningUser(user);
    setSelectedListings(new Set());
    setListingSearch('');
    setLoadingAssign(true);
    try {
      const config = { headers: { Authorization: `Bearer ${getToken()}` } };
      const [allRes, userRes] = await Promise.all([
        axios.get(`${API}/admin/listings/all`, config),
        axios.get(`${API}/admin/users/${user.id}/listings`, config)
      ]);
      setAllListings(allRes.data);
      setUserListings(userRes.data);
      setSelectedListings(new Set(userRes.data.map(l => l.id)));
    } catch (error) { console.error('Error loading listings:', error); }
    finally { setLoadingAssign(false); }
  };

  const handleAssignSave = async () => {
    if (!assigningUser) return;
    setLoadingAssign(true);
    try {
      const newIds = Array.from(selectedListings);
      const oldIds = userListings.map(l => l.id);
      
      // Assign selected listings
      if (newIds.length > 0) {
        await axios.post(`${API}/admin/users/${assigningUser.id}/assign-listings`, 
          { listing_ids: newIds },
          { headers: { Authorization: `Bearer ${getToken()}` } }
        );
      }
      
      // Unassign removed listings (set user_id to null)
      const removedIds = oldIds.filter(id => !selectedListings.has(id));
      if (removedIds.length > 0) {
        for (const lid of removedIds) {
          await axios.put(`${API}/admin/listings/${lid}`, { user_id: null }, 
            { headers: { Authorization: `Bearer ${getToken()}` } }
          );
        }
      }
      
      setAssigningUser(null);
      fetchUsers(userSearch, userCityFilter);
    } catch (error) {
      console.error('Error assigning listings:', error);
      alert('Failed to assign listings');
    } finally { setLoadingAssign(false); }
  };

  const toggleListing = (id) => {
    setSelectedListings(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const filteredListings = allListings.filter(l => 
    !listingSearch || 
    l.title?.toLowerCase().includes(listingSearch.toLowerCase()) ||
    l.city?.toLowerCase().includes(listingSearch.toLowerCase()) ||
    l.seller_name?.toLowerCase().includes(listingSearch.toLowerCase())
  );

  const exportUsersToCSV = () => {
    if (users.length === 0) return;
    setExporting(true);
    try {
      const headers = ['Name', 'Email', 'Phone', 'City', 'Properties Listed', 'Saved Properties', 'Enquiries', 'Joined Date'];
      const rows = users.map(user => [
        user.name || 'N/A', user.email || 'N/A', user.phone || 'N/A', user.city || 'N/A',
        user.properties_count || 0, user.saved_count || 0, user.enquiries_count || 0,
        user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'
      ]);
      const csvContent = [headers.join(','), ...rows.map(row => row.map(cell => `"${cell}"`).join(','))].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.setAttribute('href', URL.createObjectURL(blob));
      link.setAttribute('download', `proptick_users_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export users. Please try again.');
    } finally { setExporting(false); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl sm:text-3xl font-semibold">Registered Users</h2>
        <div className="flex items-center gap-3">
          {users.length > 0 && (
            <button onClick={exportUsersToCSV} disabled={exporting} className="flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-green-500/20" data-testid="export-users-btn">
              {exporting ? (<><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Exporting...</>) : (<><Download className="w-5 h-5" /> Export CSV</>)}
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input type="text" placeholder="Search by name or email..." value={userSearch} onChange={(e) => setUserSearch(e.target.value)} className="w-full h-10 pl-10 pr-4 rounded-xl border border-slate-200 bg-white text-sm focus:ring-2 focus:ring-blue-100 focus:border-blue-500 transition-all" data-testid="user-search-input" />
        </div>
        <div className="relative min-w-[180px]">
          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input type="text" placeholder="Filter by city..." value={userCityFilter} onChange={(e) => setUserCityFilter(e.target.value)} className="w-full h-10 pl-10 pr-4 rounded-xl border border-slate-200 bg-white text-sm focus:ring-2 focus:ring-blue-100 focus:border-blue-500 transition-all" data-testid="user-city-filter" />
        </div>
        {(userSearch || userCityFilter) && (
          <button onClick={() => { setUserSearch(''); setUserCityFilter(''); }} className="h-10 px-4 rounded-xl border border-slate-200 text-sm text-slate-600 hover:bg-slate-50 transition-all" data-testid="clear-user-filters">Clear</button>
        )}
      </div>

      {users.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
          <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-slate-700 mb-2">{userSearch || userCityFilter ? 'No users match your search' : 'No Users Yet'}</h3>
          <p className="text-slate-500">{userSearch || userCityFilter ? 'Try adjusting your filters' : 'Registered users will appear here'}</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-6 py-4 font-semibold text-slate-700">User</th>
                  <th className="text-left px-6 py-4 font-semibold text-slate-700">Contact</th>
                  <th className="text-left px-6 py-4 font-semibold text-slate-700">City</th>
                  <th className="text-center px-6 py-4 font-semibold text-slate-700">Properties</th>
                  <th className="text-center px-6 py-4 font-semibold text-slate-700">Saved</th>
                  <th className="text-center px-6 py-4 font-semibold text-slate-700">Enquiries</th>
                  <th className="text-left px-6 py-4 font-semibold text-slate-700">Joined</th>
                  <th className="text-right px-6 py-4 font-semibold text-slate-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold">{user.name?.charAt(0).toUpperCase() || 'U'}</div>
                        <div>
                          <p className="font-medium text-slate-900">{user.name || 'Unknown'}</p>
                          <p className="text-sm text-slate-500">ID: {user.id?.substring(0, 8)}...</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        {user.email && <div className="flex items-center gap-2 text-sm"><Mail className="w-4 h-4 text-slate-400" /><span className="text-slate-700">{user.email}</span></div>}
                        {user.phone && <div className="flex items-center gap-2 text-sm"><Phone className="w-4 h-4 text-slate-400" /><span className="text-slate-600">{user.phone}</span></div>}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {user.city ? (<div className="flex items-center gap-1.5 text-sm text-slate-700"><MapPin className="w-4 h-4 text-slate-400" /><span>{user.city}</span></div>) : (<span className="text-sm text-slate-400">--</span>)}
                    </td>
                    <td className="px-6 py-4 text-center"><div className="inline-flex items-center gap-1 px-3 py-1 bg-blue-50 text-blue-700 rounded-full"><Building2 className="w-4 h-4" /><span className="font-medium">{user.properties_count || 0}</span></div></td>
                    <td className="px-6 py-4 text-center"><div className="inline-flex items-center gap-1 px-3 py-1 bg-red-50 text-red-600 rounded-full"><Heart className="w-4 h-4" /><span className="font-medium">{user.saved_count || 0}</span></div></td>
                    <td className="px-6 py-4 text-center"><div className="inline-flex items-center gap-1 px-3 py-1 bg-green-50 text-green-700 rounded-full"><MessageSquare className="w-4 h-4" /><span className="font-medium">{user.enquiries_count || 0}</span></div></td>
                    <td className="px-6 py-4"><div className="flex items-center gap-2 text-sm text-slate-600"><Calendar className="w-4 h-4 text-slate-400" /><span>{user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}</span></div></td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => openAssignModal(user)}
                          className="h-8 px-3 rounded-lg bg-blue-50 text-blue-700 text-xs font-medium hover:bg-blue-100 transition-all flex items-center gap-1"
                          data-testid={`assign-props-${user.id}`}
                        >
                          <Plus className="w-3.5 h-3.5" /> Properties
                        </button>
                        <button
                          onClick={() => { setResetUser(user); setResetPassword(''); }}
                          className="h-8 px-3 rounded-lg bg-amber-50 text-amber-700 text-xs font-medium hover:bg-amber-100 transition-all flex items-center gap-1"
                          data-testid={`reset-pwd-${user.id}`}
                        >
                          <KeyRound className="w-3.5 h-3.5" /> Reset Pwd
                        </button>
                        <button
                          onClick={() => handleDeleteUser(user.id, user.name)}
                          className="h-8 px-3 rounded-lg bg-red-50 text-red-600 text-xs font-medium hover:bg-red-100 transition-all flex items-center gap-1"
                          data-testid={`delete-user-${user.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="bg-slate-50 border-t border-slate-200 px-6 py-4">
            <p className="text-sm text-slate-600">Showing: <span className="font-semibold text-slate-900">{users.length} users</span></p>
          </div>
        </div>
      )}

      {/* Assign Properties Modal */}
      {assigningUser && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setAssigningUser(null)}>
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()} data-testid="assign-properties-modal">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-xl font-bold">Assign Properties to {assigningUser.name}</h3>
                <button onClick={() => setAssigningUser(null)} className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center hover:bg-slate-200"><X className="w-4 h-4" /></button>
              </div>
              <p className="text-sm text-slate-500">{assigningUser.phone} · {assigningUser.email || 'No email'}</p>
              <div className="mt-4 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search listings by title, city, or seller..."
                  value={listingSearch}
                  onChange={e => setListingSearch(e.target.value)}
                  className="w-full h-10 pl-10 pr-4 rounded-lg border border-slate-200 text-sm focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
                  data-testid="assign-listing-search"
                />
              </div>
              <p className="text-xs text-slate-400 mt-2">{selectedListings.size} properties selected</p>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4">
              {loadingAssign ? (
                <div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>
              ) : (
                <div className="space-y-1.5">
                  {filteredListings.map(listing => (
                    <label
                      key={listing.id}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl cursor-pointer transition-all ${selectedListings.has(listing.id) ? 'bg-blue-50 border border-blue-200' : 'bg-slate-50 border border-transparent hover:bg-slate-100'}`}
                      data-testid={`assign-listing-${listing.id}`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedListings.has(listing.id)}
                        onChange={() => toggleListing(listing.id)}
                        className="w-4 h-4 rounded border-slate-300 accent-blue-600"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{listing.title}</p>
                        <p className="text-xs text-slate-500">{listing.city} · {listing.seller_name || 'No seller'} · <span className="capitalize">{listing.status}</span></p>
                      </div>
                      {selectedListings.has(listing.id) && <Check className="w-4 h-4 text-blue-600 flex-shrink-0" />}
                    </label>
                  ))}
                  {filteredListings.length === 0 && (
                    <p className="text-center text-slate-400 py-8">No listings found</p>
                  )}
                </div>
              )}
            </div>

            <div className="p-4 border-t border-slate-200 flex gap-3">
              <button onClick={() => setAssigningUser(null)} className="h-11 px-6 rounded-lg bg-slate-100 text-slate-700 font-medium hover:bg-slate-200 transition-all">Cancel</button>
              <button
                onClick={handleAssignSave}
                disabled={loadingAssign}
                className="flex-1 h-11 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="assign-save-btn"
              >
                {loadingAssign ? 'Saving...' : `Save (${selectedListings.size} properties)`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {resetUser && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setResetUser(null)}>
          <div className="bg-white rounded-2xl w-full max-w-sm p-6" onClick={e => e.stopPropagation()} data-testid="reset-password-modal">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <KeyRound className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold">Reset Password</h3>
                <p className="text-sm text-slate-500">{resetUser.name} · {resetUser.phone || resetUser.email}</p>
              </div>
            </div>
            <div className="mb-4">
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">New Password</label>
              <input
                type="text"
                placeholder="Enter new password (min 6 chars)"
                value={resetPassword}
                onChange={(e) => setResetPassword(e.target.value)}
                className="w-full h-11 px-4 rounded-lg border border-slate-200 text-sm focus:ring-2 focus:ring-amber-100 focus:border-amber-500 transition-all"
                data-testid="reset-password-input"
              />
            </div>
            <div className="flex gap-3">
              <button onClick={() => setResetUser(null)} className="flex-1 h-11 rounded-lg bg-slate-100 text-slate-700 font-medium hover:bg-slate-200 transition-all">Cancel</button>
              <button
                onClick={handleResetPassword}
                disabled={resetting || resetPassword.length < 6}
                className="flex-1 h-11 rounded-lg bg-amber-600 text-white font-medium hover:bg-amber-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="reset-password-submit"
              >
                {resetting ? 'Resetting...' : 'Reset Password'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersTab;
